import { Registry, Router } from '@lightningjs/sdk';
import LoginRadiusService from './LoginRadiusService';
import { generateUUIDv4 } from 'support/generalUtils';
import {
  ErrorCode,
  reportError,
  reportSignUpMagicLink,
} from 'services/analytics/reportingServicePage';
import BackgroundingService from './BackgroundingService';
import { PageId } from 'types/pageId';
import { getPageIdFromHash } from 'support/routerUtils';

const POLL_TIMEOUT = 10 * 1000;
export default class SmartLoginService {
  private loginRadiusService: LoginRadiusService;
  private pingTimeoutId: number | null = null;
  private guid: string | null = null;
  private successCallback: (() => void) | null = null;
  private pollStarted = false;
  private qrCode: string | null = null;
  private signUpEmail: string | null = null;
  private signUpMarketingConsent: boolean | null = null;

  constructor(loginRadiusService: LoginRadiusService) {
    this.loginRadiusService = loginRadiusService;
  }

  init(backgroundingService: BackgroundingService) {
    backgroundingService.addBackgroundingAction(
      'SmartLoginServiceBackgrounding',
      this.onBackgrounding.bind(this),
    );
    backgroundingService.addForegroundingAction(
      'SmartLoginServiceForegrounding',
      this.onForegrounding.bind(this),
    );
  }

  async sendSignInEmail(email: string) {
    this.qrCode = null;
    this.guid = generateUUIDv4();
    return this.loginRadiusService.smartLoginRequest(this.guid, email);
  }

  async sendSignUpEmail(email: string, optIn: boolean) {
    this.guid = null;
    this.signUpEmail = email;
    this.signUpMarketingConsent = optIn;
    const { code } = await this.loginRadiusService.generateQrCode();
    this.qrCode = code;
    return this.loginRadiusService.smartRegisterRequest(email, code, optIn);
  }

  startPoll(successCallback: () => void) {
    this.successCallback = successCallback;

    this.pollStarted = true;

    this.poll();
  }

  reset() {
    this.clearTimeout();
    this.guid = null;
    this.qrCode = null;
    this.successCallback = null;
    this.pollStarted = false;
    this.signUpEmail = null;
    this.signUpMarketingConsent = null;
  }

  private poll() {
    this.pingTimeoutId = Registry.setTimeout(async () => {
      if (!this.successCallback) return;

      try {
        let isSuccess = false;
        let isSignUp = false;
        if (this.guid) {
          isSuccess = !!(await this.loginRadiusService.smartLoginPing(
            this.guid,
          ));
        } else if (this.qrCode) {
          isSuccess = !!(await this.loginRadiusService.getAccessTokenFromQrCode(
            this.qrCode,
          ));
          isSignUp = true;
        }

        if (isSuccess) {
          return this.pollSuccess(isSignUp);
        }
      } catch (e) {
        // report but don't throw to allow poll to continue
        reportError({ error: e, userFacing: false, code: ErrorCode.c39 });
      } finally {
        this.poll();
      }
    }, POLL_TIMEOUT);
  }

  private pollSuccess(isSignUp: boolean) {
    // Track sign-up analytics if this was a magic link sign-up
    if (isSignUp && this.signUpEmail && this.signUpMarketingConsent !== null) {
      reportSignUpMagicLink(
        this.signUpEmail,
        this.signUpMarketingConsent,
        getPageIdFromHash(Router.getActiveHash() ?? '') as PageId,
      );
    }

    this.pollStarted = false;
    this.successCallback?.();
    this.reset();
  }

  private onBackgrounding() {
    this.clearTimeout();
  }

  private clearTimeout() {
    if (this.pingTimeoutId === null) return;

    Registry.clearTimeout(this.pingTimeoutId);
    this.pingTimeoutId = null;
  }

  private onForegrounding() {
    if (this.pollStarted) {
      this.poll();
    }
  }
}
